<script lang="ts">
	import { onMount } from "svelte";
	import Swal from "sweetalert2";
	import { BoardService } from "./board.service";
	import type { Entry } from "./model";
	import { COLORS,DEFAULT_ENTRIES } from "./model";
	import { UserService } from "./user.service";

	const boardService = BoardService.getInstance();
	const userService = UserService.getInstance();

	let entries = [];
	let lanes = [];

	onMount(() => {
		loadBoard();
		userService.onAuthStateChange(() => {
			loadBoard();
		});
	});

	async function loadBoard() {
		try {
			entries = (await boardService.findBoard()) ?? DEFAULT_ENTRIES;
		} catch (e) {
			alert(e.message);
		}
		lanes = [...new Set(entries.map((e) => e.lane))].sort();
	}

	const SwalPopup = Swal.mixin({
		showCloseButton: true,
		showClass: {
			backdrop: "swal2-noanimation", // disable backdrop animation
			popup: "", // disable popup animation
		},
		hideClass: {
			popup: "", // disable popup fade-out animation
		},
	});

	let currentDragOver: Entry = null;
	let currentDragging: Entry = null;

	async function saveAndUpdateEntries() {
		try {
			await boardService.saveBoard(entries);
		} catch (e) {
			alert(e.message);
		}
		entries = entries;
	}

	async function editLane(lane: string) {
		const newLane = await promptText("Edit", lane);
		if (!newLane || lanes.includes(newLane)) return;
		entries.forEach((e) => {
			if (e.lane === lane) e.lane = newLane;
		});
		lanes = [...new Set(entries.map((e) => e.lane))].sort();
		saveAndUpdateEntries();
	}

	async function editEntryTitle(entry: Entry) {
		const newTitle = await promptText("Edit", entry.title);
		if (newTitle == null) return;
		if (newTitle === "") {
			entries = entries.filter((e) => e.id !== entry.id);
		} else {
			entry.title = newTitle;
		}
		saveAndUpdateEntries();
	}

	async function addEntry(lane: string) {
		const title = await promptText("Add Entry");
		if (!title) return;
		const id = entries.map((e) => e.id).sort((a, b) => b - a)[0] + 1 || 0; // next free id
		entries = [...entries, { id, lane, title, color: COLORS[0] }];
		saveAndUpdateEntries();
	}

	async function addLane() {
		const lane = await promptText("Add lane");
		if (!lane || lanes.includes(lane)) return;
		lanes = [...lanes, lane];
	}

	function rotateColor(entry) {
		const idx = COLORS.findIndex((color) => color === entry.color) + 1;
		entry.color = COLORS[idx >= COLORS.length ? 0 : idx];
		saveAndUpdateEntries();
	}

	function dragstart(event, entry) {
		event.dataTransfer.setData("text/plain", JSON.stringify(entry));
		currentDragging = entry;
	}

	function dragenter(event, entry) {
		currentDragOver = entry;
	}

	function dragexit(event, entry) {
		currentDragOver = null;
	}

	function drop(event, lane) {
		event.preventDefault();
		currentDragging = null;
		const entry = JSON.parse(event.dataTransfer.getData("text/plain"));
		if (currentDragOver.id !== entry.id) {
			entry.lane = lane;
			entries = entries.filter((e) => e.id !== entry.id);
			const dragOverIdx = entries.findIndex((e) => e.id === currentDragOver.id);
			entries.splice(dragOverIdx + 1, 0, entry);
			saveAndUpdateEntries();
		}
		currentDragOver = null;
	}

	function allowDrop(event) {
		event.preventDefault();
	}

	async function signUp() {
		const email = await promptEmail();
		if (!email) return;
		const password = await promptPassword();
		if (!password) return;
		try {
			await userService.signUp(email, password);
			await Swal.fire({
				title: "Good stuff",
				icon: "success",
				text: "Nice, check your inbox",
			});
			window.location.reload();
		} catch (e) {
			await Swal.fire({
				title: "Error",
				icon: "error",
				text: e.message,
			});
		}
	}

	async function signIn() {
		const email = await promptEmail();
		if (!email) return;
		const password = await promptPassword();
		if (!password) return;
		try {
			await userService.signIn(email, password);
			window.location.reload();
		} catch (e) {
			await Swal.fire({
				title: "Error",
				icon: "error",
				text: e.message,
			});
		}
	}

	async function signOut() {
		await userService.signOut();
		window.location.reload();
	}

	async function promptText(title?: string, initialValue?: string) {
		const { value } = await SwalPopup.fire({
			title: title,
			input: "text",
			inputPlaceholder: title,
			inputValue: initialValue,
		});
		return value;
	}
	async function promptEmail() {
		const { value } = await SwalPopup.fire({
			title: "Email",
			input: "email",
			inputPlaceholder: "Enter your email",
		});
		return value;
	}
	async function promptPassword() {
		const { value } = await SwalPopup.fire({
			title: "Password",
			input: "password",
			inputPlaceholder: "Enter your password",
		});
		return value;
	}
</script>

<main>
	<div id="menu">
		{#if !userService.currentUser()}
			<button on:click={signUp}>sign up</button>
			<button on:click={signIn}>sign in</button>
		{:else}
			<button on:click={signOut}>sign out</button>
		{/if}
		<button id="add-lane" on:click={addLane}>+</button>
	</div>
	<div id="board">
		{#each lanes as lane}
			<div class="lane" on:drop={(event) => drop(event, lane)} on:dragover={allowDrop}>
				<h3
					on:contextmenu={(event) => {
						event.preventDefault();
						editLane(lane);
					}}
				>
					{lane}
				</h3>
				<button class="add-entry" on:click={() => addEntry(lane)}>+</button>
				{#each entries.filter((e) => e.lane === lane) as entry}
					<div
						class="entry-wrapper"
						class:dragover={entry.id === currentDragOver?.id}
						class:dragging={entry.id === currentDragging?.id}
					>
						<div
							class="entry"
							style="background:{entry.color}"
							on:click={() => rotateColor(entry)}
							on:contextmenu={(event) => {
								event.preventDefault();
								editEntryTitle(entry);
							}}
							on:dragstart={(event) => dragstart(event, entry)}
							on:dragenter={(event) => dragenter(event, entry)}
							on:dragexit={(event) => dragexit(event, entry)}
							draggable={true}
						>
							{entry.title}
						</div>
						<div
							class="edit-button"
							on:click={(event) => {
								editEntryTitle(entry);
							}}
						>
							📝
						</div>
					</div>
				{/each}
			</div>
		{/each}
	</div>
</main>

<style>
	main {
		text-align: center;
		height: 100%;
		min-width: min-content; /* trying to fix some weird mobile overflow issue */
		position: relative;
	}

	#board {
		display: flex;
		justify-content: space-evenly;
		min-height: 100%;
		overflow: auto;
		min-width: min-content; /* trying to fix some weird mobile overflow issue */
	}

	.lane {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		flex-shrink: 1;
		flex-basis: 0;
		padding: 0 1em;
		min-height: 100%;
	}

	.lane:nth-child(n + 2) {
		border-left: 1px solid rgb(211, 211, 211);
	}

	.entry {
		padding: 1em;
		margin: 0.3em 0;
		cursor: pointer;
		box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
	}

	.entry-wrapper {
		position: relative;
	}

	.edit-button {
		display: none;
	}

	.dragover::after {
		content: "";
		display: block;
		border: 1px dashed rgb(0, 0, 0);
		width: 100%;
		margin: 0.6em 0 0.3em 0;
		height: 3em;
	}

	.dragging {
		opacity: 0.3;
	}

	.dragging::after {
		display: none;
	}

	button {
		cursor: pointer;
	}

	#menu {
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
	}

	#menu button {
		height: 2em;
		font-size: 0.8em;
		margin: 0;
		padding: 0 0.5em;
	}

	#menu #add-lane {
		width: 2em;
		padding: 0;
	}

	.add-entry {
		min-height: 2em;
		padding: 0;
		font-size: 0.8em;
	}

	@media only screen and (max-width: 900px) {
		.edit-button {
			display: inline-block;
			font-size: 0.7rem;
			position: absolute;
			right: 0;
			bottom: 0;
			cursor: pointer;
		}
	}
</style>
